import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["svg", "overlay", "tooltip"]
  static values = {
    series: Array,
    unit: { type: String, default: "" },
    bounds: { type: Object, default: {} }
  }

  connect() {
    const b = this.boundsValue
    this.graphLeft = b.left || 50
    this.graphRight = b.right || 595
    this.graphTop = b.top || 10
    this.graphBottom = b.bottom || 195
    this.svgWidth = b.svgWidth || 600
    this.timeMin = b.timeMin || 0
    this.timeMax = b.timeMax || 0
  }

  showTooltip(event) {
    const svgX = this.toSvgX(event)
    if (svgX < this.graphLeft || svgX > this.graphRight) {
      this.hideTooltip()
      return
    }

    const ratio = (svgX - this.graphLeft) / (this.graphRight - this.graphLeft)
    this.buildTooltipContent(ratio)
    this.positionTooltip(event)
  }

  hideTooltip() {
    this.tooltipTarget.classList.add("hidden")
  }

  // private

  toSvgX(event) {
    const rect = this.svgTarget.getBoundingClientRect()
    return ((event.clientX - rect.left) / rect.width) * this.svgWidth
  }

  buildTooltipContent(ratio) {
    const tooltip = this.tooltipTarget
    tooltip.textContent = ""

    const targetTime = this.timeMin + ratio * (this.timeMax - this.timeMin)
    const timestamp = (this.timeMax > this.timeMin) ? targetTime : null

    this.seriesValue.forEach(s => {
      if (!s.data || s.data.length === 0) return

      const point = this.findNearestPoint(s.data, targetTime)
      if (!point) return

      const row = document.createElement("div")
      row.className = "flex items-center gap-1.5 py-0.5"

      const colorDot = document.createElement("span")
      colorDot.className = "inline-block w-2 h-2 rounded-sm flex-shrink-0"
      colorDot.style.backgroundColor = s.color

      const nameLabel = document.createElement("span")
      nameLabel.className = "text-gray-300"
      nameLabel.textContent = s.name

      const valueLabel = document.createElement("span")
      valueLabel.className = "ml-auto font-medium"
      valueLabel.textContent = `${this.formatValue(point.value)}${this.unitValue}`

      row.append(colorDot, nameLabel, valueLabel)
      tooltip.appendChild(row)
    })

    if (timestamp !== null) {
      const date = new Date(timestamp * 1000)
      const dateStr = date.toLocaleString("ja-JP", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      })

      const header = document.createElement("div")
      header.className = "text-gray-400 border-b border-gray-600 pb-1 mb-1"
      header.textContent = dateStr
      tooltip.prepend(header)
    }

    tooltip.classList.remove("hidden")
  }

  positionTooltip(event) {
    const tooltip = this.tooltipTarget
    const containerRect = this.element.getBoundingClientRect()
    const tooltipWidth = tooltip.offsetWidth

    let left = event.clientX - containerRect.left + 12
    if (left + tooltipWidth > containerRect.width) {
      left = event.clientX - containerRect.left - tooltipWidth - 12
    }

    let top = event.clientY - containerRect.top - tooltip.offsetHeight - 8
    if (top < 0) top = event.clientY - containerRect.top + 12

    tooltip.style.left = `${left}px`
    tooltip.style.top = `${top}px`
  }

  findNearestPoint(data, targetTime) {
    let low = 0
    let high = data.length - 1

    while (low < high) {
      const mid = (low + high) >> 1
      if (data[mid].time < targetTime) {
        low = mid + 1
      } else {
        high = mid
      }
    }

    if (low === 0) return data[0]
    const prev = data[low - 1]
    const curr = data[low]
    return (targetTime - prev.time <= curr.time - targetTime) ? prev : curr
  }

  formatValue(value) {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
    if (Number.isInteger(value)) return value.toString()
    return value.toFixed(2)
  }
}
